import { MINUTES_PER_DAY } from './balance';
import type { GameTime, MatchConfig } from './types';

/** Convert a real timestamp to game time for a running match. */
export function realToGameTime(
  nowMs: number,
  startedAtMs: number,
  config: Pick<MatchConfig, 'timeScale'>,
): GameTime {
  return Math.max(0, Math.floor(((nowMs - startedAtMs) / 60_000) * config.timeScale));
}

/** Convert a game time to the real timestamp when it occurs. */
export function gameToRealTime(
  time: GameTime,
  startedAtMs: number,
  config: Pick<MatchConfig, 'timeScale'>,
): number {
  return startedAtMs + (time / config.timeScale) * 60_000;
}

/** 1-based day number for a game time. */
export function dayOf(time: GameTime): number {
  return Math.floor(time / MINUTES_PER_DAY) + 1;
}

/** Match phase index for scoring multipliers: 0 expansion, 1 contest, 2 crescendo. */
export function phaseOf(time: GameTime, durationDays: number): 0 | 1 | 2 {
  const day = dayOf(time);
  const expansionEnd = Math.max(1, Math.round(durationDays * (2 / 7)));
  const contestEnd = Math.max(expansionEnd + 1, Math.round(durationDays * (5 / 7)));
  if (day <= expansionEnd) return 0;
  if (day <= contestEnd) return 1;
  return 2;
}

/** "14:20" style clock for a game time, in match-local time (match starts at 08:00 on day 1). */
export function clockOf(time: GameTime, startHour = 8): string {
  const minutes = (time + startHour * 60) % MINUTES_PER_DAY;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Human duration like "3 h 10 m", "3 h" or "45 m". The one formatter for durations in event text
 * (the client uses the same spelling).
 */
export function formatDuration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem} m`;
  if (rem === 0) return `${h} h`;
  return `${h} h ${rem} m`;
}

/** "Day 3" (match days are 1-based and capitalised in all player-facing text). */
export function dayLabel(time: GameTime): string {
  return `Day ${dayOf(time)}`;
}
